import { formatPhone, TICKET_SOURCE_LABELS, type TicketDetails, type TicketStatus } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronLeft, Phone, Plus, Search, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Comments } from '@/components/comments';
import { EmptyPanel, Pager } from '@/components/crm-bits';
import { ChatPanel } from '@/components/crm-chat';
import {
  EquipmentNav,
  HistoryPanel,
  Person,
  PriorityBadge,
  Row,
  SlaMark,
  TicketStatusBadge,
} from '@/components/equipment-bits';
import { ReasonDialog, TicketFormDialog } from '@/components/equipment-forms';
import { TicketTable } from '@/components/equipment-tables';
import { Button, ErrorBox, Input, PageHeader, Segmented, Select, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { chatQuery } from '@/lib/crm';
import {
  helpdeskCountersQuery,
  type TicketsFilter,
  ticketHistoryQuery,
  ticketQuery,
  ticketsQuery,
} from '@/lib/equipment';
import { formatMoment } from '@/lib/format';

export interface HelpdeskSearch {
  scope?: 'all' | 'mine' | 'free' | 'created';
  state?: 'overdue' | 'done' | 'all';
  q?: string;
  page?: number;
}

export function TicketsPage() {
  const search = useSearch({ strict: false }) as HelpdeskSearch;
  const navigate = useNavigate();
  const can = useCan();
  const worker = can('helpdesk.access');
  const seesAll = worker || can('equipment.read') || can('production.manage');
  const counters = useQuery(helpdeskCountersQuery);
  const [q, setQ] = useState(search.q ?? '');
  const [creating, setCreating] = useState(false);
  const debounce = useRef<number | undefined>(undefined);
  // Сервису по умолчанию — «мои», остальным — «все видимые» (для автора это его заявки)
  const scope: TicketsFilter['scope'] = search.scope ?? (worker ? 'mine' : 'all');
  const state = search.state ?? 'open';
  const list = useQuery(ticketsQuery({ scope, state, q: search.q, page: search.page ?? 1 }));
  const setSearch = (patch: Partial<HelpdeskSearch>) =>
    navigate({ to: '/helpdesk', search: { ...search, page: undefined, ...patch }, replace: true });
  const onQuery = (value: string) => {
    setQ(value);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => setSearch({ q: value.trim() || undefined }), 250);
  };

  const c = counters.data;
  const scopeOptions = worker
    ? [
        { value: 'mine' as const, label: c?.mine ? `Мои · ${c.mine}` : 'Мои' },
        { value: 'free' as const, label: c?.free ? `Без исполнителя · ${c.free}` : 'Без исполнителя' },
        { value: 'all' as const, label: 'Все' },
        { value: 'created' as const, label: 'Я создал' },
      ]
    : seesAll
      ? [
          { value: 'all' as const, label: 'Все' },
          { value: 'created' as const, label: 'Я создал' },
        ]
      : null;

  return (
    <div>
      <PageHeader
        title="Сервисные заявки"
        description={
          worker && c
            ? c.overdue
              ? `Просрочено: ${c.overdue} — мои и без исполнителя`
              : 'Обслуживание и ремонт оборудования клиентов'
            : 'Обслуживание и ремонт оборудования клиентов'
        }
        actions={
          (can('helpdesk.ticket.create') || worker) && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              Заявка
            </Button>
          )
        }
      />
      {seesAll && <EquipmentNav />}
      {creating && <TicketFormDialog onClose={() => setCreating(false)} />}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {scopeOptions && (
          <Segmented
            label="Чьи заявки"
            value={scope}
            onChange={(v) =>
              setSearch({
                scope: v === (worker ? 'mine' : 'all') ? undefined : (v as HelpdeskSearch['scope']),
              })
            }
            options={scopeOptions}
          />
        )}
        <Select
          aria-label="Состояние"
          value={state}
          onChange={(e) =>
            setSearch({
              state: e.target.value === 'open' ? undefined : (e.target.value as HelpdeskSearch['state']),
            })
          }
          className="w-[170px]"
        >
          <option value="open">Открытые</option>
          <option value="overdue">Просроченные</option>
          <option value="done">Закрытые</option>
          <option value="all">Все</option>
        </Select>
        <div className="relative w-full max-w-[280px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Тема, клиент, заводской №"
            aria-label="Поиск заявок"
            className="pl-9"
          />
        </div>
      </div>
      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : list.data.items.length === 0 ? (
        <EmptyPanel title="Заявок нет">
          {state === 'open' ? 'Открытых заявок нет — хорошая новость.' : 'Измените фильтры.'}
        </EmptyPanel>
      ) : (
        <>
          <TicketTable items={list.data.items} />
          <Pager
            page={list.data.page}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => setSearch({ page: p })}
          />
        </>
      )}
    </div>
  );
}

// ---------- Карточка заявки ----------

export function TicketPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const can = useCan();
  const q = useQuery(ticketQuery(id));
  const history = useQuery(ticketHistoryQuery(id));
  const [dialog, setDialog] = useState<'edit' | 'done' | 'cancelled' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['helpdesk'] }),
      qc.invalidateQueries({ queryKey: ['equipment'] }),
    ]);
  const take = useMutation({
    mutationFn: () => api(`/helpdesk/tickets/${id}/take`, { method: 'POST' }),
    onSuccess: () => setError(null),
    onError: (err) => setError(err.message),
    onSettled: invalidate,
  });
  const status = useMutation({
    mutationFn: (v: { status: TicketStatus; resolution?: string }) =>
      api(`/helpdesk/tickets/${id}/status`, { method: 'POST', body: v }),
    onSuccess: () => {
      setDialog(null);
      setError(null);
    },
    onError: (err) => setError(err.message),
    onSettled: invalidate,
  });

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const t = q.data;
  const open = t.status === 'new' || t.status === 'in_progress' || t.status === 'waiting';
  const crm = can('crm.access');

  return (
    <div className="max-w-[1240px]">
      <Link
        to="/helpdesk"
        className="mb-4 inline-flex items-center gap-1 text-[13px] text-steel hover:text-ink"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Сервисные заявки
      </Link>
      <header className="mb-4 flex flex-wrap items-start gap-3">
        <div className="min-w-[240px] flex-1">
          <p className="text-[13px] tabular-nums text-steel">
            Заявка #{t.number}
            {t.category && ` · ${t.category.name}`}
          </p>
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.015em]">{t.subject}</h1>
          <p className="mt-1.5 flex flex-wrap items-center gap-2">
            <TicketStatusBadge status={t.status} />
            <PriorityBadge priority={t.priority} />
            <SlaMark ticket={t} long />
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {t.permissions.canTake && (
            <Button onClick={() => take.mutate()} loading={take.isPending}>
              Взять в работу
            </Button>
          )}
          {t.permissions.canEdit && (
            <Button variant="secondary" onClick={() => setDialog('edit')}>
              Изменить
            </Button>
          )}
        </div>
      </header>

      {dialog === 'edit' && <TicketFormDialog ticket={t} onClose={() => setDialog(null)} />}
      {(dialog === 'done' || dialog === 'cancelled') && (
        <ReasonDialog
          title={dialog === 'done' ? 'Заявка выполнена' : 'Отменить заявку'}
          label={dialog === 'done' ? 'Что сделано' : 'Причина отмены'}
          placeholder={
            dialog === 'done'
              ? 'Заменили регулятор давления, проверили на 1800 бар'
              : 'Клиент решил сам, дубль…'
          }
          submitLabel={dialog === 'done' ? 'Закрыть заявку' : 'Отменить заявку'}
          loading={status.isPending}
          error={error}
          onSubmit={(resolution) => status.mutate({ status: dialog, resolution })}
          onClose={() => {
            setDialog(null);
            setError(null);
          }}
        />
      )}

      {(t.permissions.canWork || t.permissions.canCancel) && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {t.permissions.canWork && (
            <fieldset className="inline-flex flex-wrap gap-1 rounded-md border border-line-strong bg-surface p-0.5">
              <legend className="sr-only">Статус заявки</legend>
              {(['new', 'in_progress', 'waiting'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={t.status === s}
                  disabled={t.status === s || status.isPending}
                  onClick={() => status.mutate({ status: s })}
                  className={clsx(
                    'h-8 rounded px-3 text-[13.5px]',
                    t.status === s ? 'bg-graphite text-white' : 'text-ink-soft hover:bg-sunken',
                  )}
                >
                  {s === 'new' ? 'Новая' : s === 'in_progress' ? 'В работе' : 'Ждём клиента'}
                </button>
              ))}
            </fieldset>
          )}
          {t.permissions.canWork && open && (
            <Button size="sm" onClick={() => setDialog('done')}>
              Выполнена
            </Button>
          )}
          {t.permissions.canCancel && (
            <Button size="sm" variant="ghost" onClick={() => setDialog('cancelled')}>
              Отменить заявку
            </Button>
          )}
          {error && !dialog && (
            <p role="alert" className="w-full text-[13.5px] text-danger">
              {error}
            </p>
          )}
        </div>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-6">
          {t.resolution && !open && (
            <section
              className={clsx(
                'panel border-l-[3px] px-5 py-4',
                t.status === 'done' ? 'border-l-ok' : 'border-l-line-strong',
              )}
            >
              <h2 className="mb-1 font-semibold">{t.status === 'done' ? 'Что сделано' : 'Причина отмены'}</h2>
              <p className="max-w-[75ch] whitespace-pre-wrap break-words">{t.resolution}</p>
            </section>
          )}
          <section className="panel px-5 py-4">
            <h2 className="mb-1.5 font-semibold">Подробности</h2>
            {t.description ? (
              <p className="max-w-[75ch] whitespace-pre-wrap break-words">{t.description}</p>
            ) : (
              <p className="text-steel">Не указаны.</p>
            )}
          </section>
          {t.chat && crm && <TicketChat chatId={t.chat.id} />}
          <Comments entityType="ticket" entityId={t.id} />
        </div>
        <aside className="flex flex-col gap-6">
          <section className="panel px-5 py-4">
            <dl className="flex flex-col gap-3.5">
              <Row label="Клиент">
                {t.contact ? (
                  crm ? (
                    <Link to="/crm/contacts/$id" params={{ id: t.contact.id }} className="link font-medium">
                      {t.contact.fullName}
                    </Link>
                  ) : (
                    <span className="font-medium">{t.contact.fullName}</span>
                  )
                ) : !t.company ? (
                  <span className="text-steel">не указан</span>
                ) : null}
                {t.company &&
                  (crm ? (
                    <Link to="/crm/companies/$id" params={{ id: t.company.id }} className="link block">
                      {t.company.name}
                    </Link>
                  ) : (
                    <span className="block">{t.company.name}</span>
                  ))}
                {t.contactPhones.map((p) => (
                  <a
                    key={p}
                    href={`tel:${p}`}
                    className="mt-0.5 flex items-center gap-1.5 text-[13.5px] hover:text-link"
                  >
                    <Phone className="size-3.5 text-steel" aria-hidden />
                    {formatPhone(p)}
                  </a>
                ))}
              </Row>
              <Row label="Оборудование">
                {t.equipment ? (
                  <Link to="/equipment/$id" params={{ id: t.equipment.id }} className="link">
                    {t.equipment.title}
                  </Link>
                ) : (
                  <span className="text-steel">не указано</span>
                )}
              </Row>
              {t.deal && (
                <Row label="Сделка">
                  {crm ? (
                    <Link to="/crm/deals/$id" params={{ id: t.deal.id }} className="link">
                      #{t.deal.number} {t.deal.title}
                    </Link>
                  ) : (
                    `#${t.deal.number} ${t.deal.title}`
                  )}
                </Row>
              )}
              <Row label="Исполнитель">
                <Person user={t.assignee} empty="не назначен — возьмите в работу" />
              </Row>
              <Row label="Срок реакции">
                {formatMoment(t.reactionDueAt)}
                {t.reactedAt ? (
                  <span className="block text-[12.5px] text-steel">взята {formatMoment(t.reactedAt)}</span>
                ) : null}
              </Row>
              <Row label="Срок решения">
                {formatMoment(t.resolveDueAt)}
                {t.doneAt && (
                  <span className="block text-[12.5px] text-steel">закрыта {formatMoment(t.doneAt)}</span>
                )}
              </Row>
              <Row label="Источник">{TICKET_SOURCE_LABELS[t.source]}</Row>
              <Row label="Создана">
                {formatMoment(t.createdAt)}
                {t.createdBy && <span className="text-steel"> · {t.createdBy.fullName}</span>}
              </Row>
            </dl>
          </section>
          <HistoryPanel items={history.data} />
          {t.permissions.canWork && <DeleteTicket t={t} />}
        </aside>
      </div>
    </div>
  );
}

function TicketChat({ chatId }: { chatId: string }) {
  const chat = useQuery(chatQuery(chatId));
  if (!chat.data) return null;
  return <ChatPanel chat={chat.data} className="h-[420px]" />;
}

function DeleteTicket({ t }: { t: TicketDetails }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const del = useMutation({
    mutationFn: () => api(`/helpdesk/tickets/${t.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['helpdesk'] });
      await navigate({ to: '/helpdesk' });
    },
  });
  return confirm ? (
    <div className="panel flex flex-col gap-2 px-5 py-4">
      <p className="text-[13.5px]">
        Удалить заявку #{t.number}? Если она просто не нужна — лучше «Отменить».
      </p>
      <div className="flex gap-2">
        <Button variant="danger" size="sm" loading={del.isPending} onClick={() => del.mutate()}>
          Удалить
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
          Отмена
        </Button>
      </div>
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setConfirm(true)}
      className="inline-flex items-center gap-1.5 self-start text-[13px] text-steel hover:text-danger"
    >
      <Trash2 className="size-3.5" aria-hidden />
      Удалить заявку
    </button>
  );
}
